import { useEffect } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useDashboards } from '~/hooks/useDashboard';
import { usePortfolios } from '~/hooks/usePortfolios';
import { useRBAC } from '~/hooks/useRBAC';
import { useFeatureFlagsFromClerk } from '~/hooks/useOrganizationConfig';
import { isFeatureEnabled } from '~/config/organizationConfig';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';
import { validateAndRedirectOrg } from '~/utils/subdomain';

/**
 * Hook to handle initial route navigation based on feature flags, RBAC, and available data
 * Determines the appropriate route to navigate to when the app loads
 */
export const useInitialRoute = () => {
  const auth = useAuth();
  const { isLoaded: isOrgLoaded } = useOrganization();
  const featureFlags = useFeatureFlagsFromClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = useRBAC();
  const {
    data: dashboardsResponse,
    isLoading: dashboardsLoading,
    isError: dashboardsError,
  } = useDashboards();
  const { portfolios, loading: portfolioLoading } = usePortfolios();

  useEffect(() => {
    // Don't run this effect if we're already on a dashboard page
    if (location.pathname.startsWith('/dashboard/')) {
      return;
    }

    // Only proceed if authenticated
    if (!auth.isSignedIn) {
      return;
    }

    // Wait for organization to load so Clerk feature flags (publicMetadata.featureFlags) are available
    if (!isOrgLoaded) {
      return;
    }

    if (
      !dashboardsLoading &&
      !dashboardsError &&
      dashboardsResponse &&
      auth.isLoaded &&
      !portfolioLoading
    ) {
      // Check feature flags and RBAC access (from Clerk org publicMetadata)
      const hasPortfolioManagement =
        isFeatureEnabled(featureFlags, FEATURE_FLAGS.PORTFOLIO_MANAGEMENT) &&
        hasAccess('portfolio');
      const hasDynamicDashboard = isFeatureEnabled(
        featureFlags,
        FEATURE_FLAGS.DYNAMIC_DASHBOARD,
      );

      const dashboards = Array.isArray(dashboardsResponse)
        ? dashboardsResponse
        : [];

      // Priority 1: Portfolio Management feature enabled and user has access
      if (hasPortfolioManagement) {
        if (portfolios.length > 0) {
          // Navigate to first portfolio overview
          const firstPortfolioId = portfolios[0].id;
          navigate({
            to: '/portfolio/$portfolioId/overview',
            params: { portfolioId: firstPortfolioId },
          });
        } else {
          // No portfolios found, navigate to no-portfolios page
          navigate({ to: '/no-portfolios' });
        }
      }
      // Priority 2: Dynamic Dashboard feature enabled
      else if (hasDynamicDashboard) {
        if (dashboards.length > 0) {
          navigate({ to: `/dashboard/${dashboards[0].id}` });
        } else {
          navigate({ to: '/no-dashboards' });
        }
      }
      // Priority 3: Fund Management feature enabled – fund overview as landing page
      // Send to fund/overview when org has the flag; route beforeLoad enforces hasAccess('fund') and shows 404 if not allowed
      else if (isFeatureEnabled(featureFlags, FEATURE_FLAGS.FUND_MANAGEMENT)) {
        navigate({ to: '/fund/overview', search: { fundId: undefined } });
      }
      // Priority 4: Default - go to no-portfolios page
      else {
        navigate({ to: '/no-portfolios' });
      }

      // Validate and redirect org if needed
      validateAndRedirectOrg(auth);
    }
  }, [
    auth,
    isOrgLoaded,
    featureFlags,
    dashboardsError,
    dashboardsLoading,
    dashboardsResponse,
    location.pathname,
    navigate,
    portfolios,
    portfolioLoading,
    hasAccess,
  ]);
};
